import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { walletAPI } from '../utils/apiClient';
import LoadingSpinner from '../components/LoadingSpinner';
import '../styles/WalletPage.css';

const WalletPage = () => {
  const { user, updateUserBalance } = useAuth();
  
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await walletAPI.getTransactions();
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      // Fallback for MVP - show some demo transactions
      setTransactions([
        { 
          id: 1, 
          type: 'add_funds', 
          amount: 10.00, 
          description: 'Welcome bonus',
          createdAt: new Date().toISOString(),
          balanceAfter: 10.00
        }
      ]);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const handleAddFunds = async (e) => {
    e.preventDefault();
    
    if (!amount || parseFloat(amount) < 1 || parseFloat(amount) > 1000) {
      setError('Amount must be between $1 and $1000');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // For MVP mode, skip Stripe and just call the backend directly
      const response = await walletAPI.addFunds({
        amount: parseFloat(amount)
      });

      if (response.data.success) {
        updateUserBalance(response.data.newBalance);
        setSuccess(`Successfully added $${amount} to your account!`);
        setAmount('');
        
        // Refresh transactions
        await fetchTransactions();
      } else {
        setError('Failed to add funds. Please try again.');
      }
      
    } catch (error) {
      console.error('Add funds error:', error);
      setError(error.response?.data?.error || 'Failed to add funds. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getTransactionIcon = (type) => {
    const icons = {
      add_funds: '💰',
      box_purchase: '📦',
      item_sale: '💎',
      refund: '↩️'
    };
    return icons[type] || '💳';
  };

  const getTransactionColor = (type) => {
    const colors = {
      add_funds: '#5CB85C',
      box_purchase: '#D9534F',
      item_sale: '#5CB85C',
      refund: '#5BC0DE'
    };
    return colors[type] || '#777';
  };

  return (
    <div className="wallet-page">
      <div className="container">
        <div className="wallet-header">
          <h1>Your Wallet</h1>
          <div className="current-balance">
            <span className="balance-label">Current Balance:</span>
            <span className="balance-amount">${parseFloat(user?.balance || 0).toFixed(2)}</span>
          </div>
        </div>

        <div className="wallet-content">
          <div className="add-funds-section">
            <h2>Add Funds</h2>
            <p>Add money to your account to purchase mystery boxes</p>
            
            <form onSubmit={handleAddFunds} className="add-funds-form">
              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  {error}
                </div>
              )}
              
              {success && (
                <div className="success-message">
                  <span className="success-icon">✅</span>
                  {success}
                </div>
              )}

              <div className="amount-input-group">
                <label htmlFor="amount">Amount ($1 - $1000)</label>
                <div className="amount-input-container">
                  <span className="currency-symbol">$</span>
                  <input
                    type="number"
                    id="amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    min="1"
                    max="1000"
                    step="0.01"
                    placeholder="0.00"
                    className="amount-input"
                    required
                  />
                </div>
              </div>

              <div className="card-input-group">
                <label>Payment Information</label>
                <div className="mvp-payment-info">
                  <p>💡 <strong>MVP Mode:</strong> This is a demo environment. Click "Add Funds" to simulate adding money to your account.</p>
                </div>
              </div>

              <button 
                type="submit" 
                disabled={loading || !amount}
                className="add-funds-btn"
              >
                {loading ? 'Processing...' : `Add $${amount || '0.00'} (Demo)`}
              </button>
            </form>

            <div className="payment-info">
              <h3>🎮 Demo Mode Information</h3>
              <div className="demo-info">
                <div className="demo-note">
                  <strong>This is a demo environment</strong>
                </div>
                <div className="demo-note">
                  No real payments are processed
                </div>
                <div className="demo-note">
                  Click "Add Funds" to simulate adding money
                </div>
              </div>
              <p className="sandbox-note">
                🔒 This is a demo environment for testing purposes only.
              </p>
            </div>
          </div>

          <div className="transactions-section">
            <h2>Transaction History</h2>
            
            {transactionsLoading ? (
              <LoadingSpinner size="small" message="Loading transactions..." />
            ) : transactions.length === 0 ? (
              <div className="empty-transactions">
                <p>No transactions yet</p>
              </div>
            ) : (
              <div className="transactions-list">
                {transactions.map((transaction) => (
                  <div key={transaction.id} className="transaction-item">
                    <div className="transaction-icon">
                      {getTransactionIcon(transaction.type)}
                    </div>
                    
                    <div className="transaction-details">
                      <div className="transaction-description">
                        {transaction.description}
                      </div>
                      <div className="transaction-date">
                        {new Date(transaction.createdAt).toLocaleString()}
                      </div>
                    </div>
                    
                    <div className="transaction-amount">
                      <span 
                        className={`amount ${transaction.amount > 0 ? 'positive' : 'negative'}`}
                        style={{ color: getTransactionColor(transaction.type) }}
                      >
                        {transaction.amount > 0 ? '+' : ''}${parseFloat(transaction.amount).toFixed(2)}
                      </span>
                      <div className="balance-after">
                        Balance: ${parseFloat(transaction.balanceAfter).toFixed(2)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="wallet-tips">
          <h3>💡 Tips</h3>
          <ul>
            <li>Add funds in larger amounts to save on transaction fees</li>
            <li>Your balance never expires</li>
            <li>Sell unwanted items to earn credits back</li>
            <li>Watch for special promotions and bonus credit events</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default WalletPage;
